import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import "@/App.css";
import "@/index.css";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { WhiteLabelProvider, useWhiteLabel } from "@/contexts/WhiteLabelContext";
// ThemeProvider removed - using original vibrant color scheme
import { requestManager } from "@/utils/requestManager";
import { cancelAllRequests } from "@/utils/managedAxios";
import { dismissAllToasts } from "@/hooks/use-toast";
import WhyChooseCLARIASPopup from "@/components/WhyChooseCLARIASPopup";
import { GuidedTour } from "@/components/GuidedTour";
import { ClariasLogo } from "@/components/ClariasLogo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import LandingPage from "@/pages/LandingPage";
import PublicLandingPage from "@/pages/PublicLandingPage";
import PublicLandingV7 from "@/pages/PublicLandingV7";
import PublicPYQExplorer from "@/pages/PublicPYQExplorer";
import FreeDiagnosticPage from "@/pages/FreeDiagnosticPage";
import DashboardPage from "@/pages/DashboardPage";
import AuthPage from "@/pages/AuthPage";
import AuthPageOTP from "@/pages/AuthPageOTP";
import ReferEarnPage from "@/pages/ReferEarnPage";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import GoogleDirectCallback from "@/pages/GoogleDirectCallback";
// import MainsAnswerPage from "@/pages/MainsAnswerPage"; // DEPRECATED: Use GS-specific suite pages instead
import GS1SuitePage from "@/pages/GS1SuitePage";
import GS2SuitePage from "@/pages/GS2SuitePage";
import GS3SuitePage from "@/pages/GS3SuitePage";
import GS4SuitePage from "@/pages/GS4SuitePage";
import CSATSuitePage from "@/pages/CSATSuitePage";
import MentorPage from "@/pages/MentorPage";
import ClarityAssistantPage from "@/pages/ClarityAssistantPage";
import FeaturesShowcasePage from "@/pages/FeaturesShowcasePage";
import CurrentAffairsPage from "@/pages/CurrentAffairsPage";
import UpgradePage from "@/pages/UpgradePage";
import EvaluationPage from "@/pages/EvaluationPage";
import NotesPage from "@/pages/NotesPage";
import PYQPage from "@/pages/PYQPage";
import TestsPage from "@/pages/TestsPage";
import GamificationPage from "@/pages/GamificationPage";
import ExpectedQuestionsPage from "@/pages/ExpectedQuestionsPage";
import TestSeriesPage from "@/pages/TestSeriesPage";
import DailyTargetsPage from "@/pages/DailyTargetsPage";
import DailyIntelligencePage from "@/pages/DailyIntelligencePage";
import DailyIntelligenceV2Page from "@/pages/DailyIntelligenceV2Page";
import EssaySuitePage from "@/pages/EssaySuitePage";
import PrelimsHubPage from "@/pages/PrelimsHubPage";
import SIPYQPage from "@/pages/SIPYQPage";
import ChapterPracticePage from "@/pages/ChapterPracticePage";
import { PrelimsMasterSubjectsPage, PrelimsMasterSubjectPage, PrelimsMasterChapterPage } from "@/pages/PrelimsMasterPages";
import PolityRadialMindMapsPage from "@/pages/PolityRadialMindMapsPage";
import MindMapPage from "@/pages/MindMapPage";
import PhoneGate from "@/components/PhoneGate";
import VisualResourcePage from "@/pages/VisualResourcePage";
import PrelimsMCQPage from "@/pages/PrelimsMCQPage";
import PrelimsCAConverterPage from "@/pages/PrelimsCAConverterPage";
import PrelimsCSATPage from "@/pages/PrelimsCSATPage";
import PrelimsTestSeriesPage from "@/pages/PrelimsTestSeriesPage";
import PrelimsTestSeriesPageNew from "@/pages/PrelimsTestSeriesPageNew";
import PrelimsNewspaperConverterPage from "@/pages/PrelimsNewspaperConverterPage";
import PrelimsHeatmapPage from "@/pages/PrelimsHeatmapPage";
import PrelimsPatternPredictionPage from "@/pages/PrelimsPatternPredictionPage";
import PrelimsTrapDetectorPage from "@/pages/PrelimsTrapDetectorPage";
import PrelimsWeakAreaAnalyticsPage from "@/pages/PrelimsWeakAreaAnalyticsPage";
import PrelimsRevisionSchedulerPage from "@/pages/PrelimsRevisionSchedulerPage";
import MainsRevisionSchedulerPage from "@/pages/MainsRevisionSchedulerPage";
import MainsHubPage from "@/pages/MainsHubPage";
import PYQPapersPage from "@/pages/PYQPapersPage";
import PYQGenomePage from "@/pages/PYQGenomePage";
import MockTest2025Page from "@/pages/MockTest2025Page";
import MaterialHubPage from "@/pages/MaterialHubPage";
import ProgressReportPage from "@/pages/ProgressReportPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import RefundPolicyPage from "@/pages/RefundPolicyPage";
import LaunchCompliancePage from "@/pages/LaunchCompliancePage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import SupportPage from "@/pages/SupportPage";
import HelpPage from "@/pages/HelpPage";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import IntelligenceEnginePage from "@/pages/IntelligenceEnginePage";
import StrategyDashboardPage from "@/pages/StrategyDashboardPage";
import ProRoute from "@/components/ProRoute";
import WeaknessMapPage from "@/pages/WeaknessMapPage";
import MyEvaluationsPage from "@/pages/MyEvaluationsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import SwitchingCostWallPage from "@/pages/SwitchingCostWallPage";
import AccountAccessPage from "@/pages/AccountAccessPage";
import ERCAPage from "@/pages/ERCAPage";
import IntelligenceCheckPage from "@/pages/IntelligenceCheckPage";
import CommandScreenPage from "@/pages/CommandScreenPage";
import CSATDiagnosticPage from "@/pages/CSATDiagnosticPage";
import GSDiagnosticPage from "@/pages/GSDiagnosticPage";
import ConceptIntelligencePage from "@/pages/ConceptIntelligencePage";
import IntelligenceDigestsPage from "@/pages/IntelligenceDigestsPage";
import DiagnosticFeedbackPage from "@/pages/DiagnosticFeedbackPage";
import OptionalHubPage from "@/pages/optional/OptionalHubPage";
import DailyChallengePage from "@/pages/DailyChallengePage";
import AnswerArenaPage from "@/pages/AnswerArenaPage";
import NationalBenchmarkPage from "@/pages/NationalBenchmarkPage";
import MistakeTrackerPage from "@/pages/MistakeTrackerPage";
import ClarityAIPage from "@/pages/ClarityAIPage";
import AnthropologySuitePage from "@/pages/optional/anthropology/AnthropologySuitePage";
import AnthropologyPaper1Page from "@/pages/optional/anthropology/Paper1Page";
import AnthropologyPaper2Page from "@/pages/optional/anthropology/Paper2Page";
import SociologySuitePage from "@/pages/optional/sociology/SociologySuitePage";
import SociologyPaper1Page from "@/pages/optional/sociology/Paper1Page";
import SociologyPaper2Page from "@/pages/optional/sociology/Paper2Page";
import PubAdminSuitePage from "@/pages/optional/pubadmin/PubAdminSuitePage";
import PubAdminPaper1Page from "@/pages/optional/pubadmin/Paper1Page";
import PubAdminPaper2Page from "@/pages/optional/pubadmin/Paper2Page";
import PSIRSuitePage from "@/pages/optional/psir/PSIRSuitePage";
import PSIRPaper1Page from "@/pages/optional/psir/Paper1Page";
import PSIRPaper2Page from "@/pages/optional/psir/Paper2Page";
import AdminDashboard from "@/pages/AdminDashboard";
import FreeAnswerPage from "@/pages/FreeAnswerPage";
import FreeAnswerAdminPage from "@/pages/FreeAnswerAdminPage";
// DarkModeToggle removed - using original vibrant color scheme
import LegalFooter from "@/components/LegalFooter";
import CookieBanner from "@/components/CookieBanner";
import BottomNav from "@/components/BottomNav";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import MobileBottomNav from "@/components/MobileBottomNav";
import ScrollToTop from "@/components/ScrollToTop";
import { API_BASE } from "@/services/api";
import ExamSelectorPage from "@/pages/ExamSelectorPage";
// Banking Pages
import BankingDashboardPage from "@/pages/banking/BankingDashboardPage";
import BankingQuantPage from "@/pages/banking/BankingQuantPage";
import BankingReasoningPage from "@/pages/banking/BankingReasoningPage";
import BankingEnglishPage from "@/pages/banking/BankingEnglishPage";
import BankingAwarenessPage from "@/pages/banking/BankingAwarenessPage";
import BankingAICoachPage from "@/pages/banking/BankingAICoachPage";
import BankingAnalyticsPage from "@/pages/banking/BankingAnalyticsPage";
import BankingPracticePage from "@/pages/banking/BankingPracticePage";
import BankingUniversalPracticePage from "@/pages/banking/BankingUniversalPracticePage";
import BankingLearnPage from "@/pages/banking/BankingLearnPage";
import BankingAwarenessLearnPage from "@/pages/banking/BankingAwarenessLearnPage";
import BankingMockTestPage from "@/pages/banking/BankingMockTestPage";
import BankingSectionalTestPage from "@/pages/banking/BankingSectionalTestPage";
import BankingMainsMockTestPage from "@/pages/banking/BankingMainsMockTestPage";
import BankingDescriptivePaperPage from "@/pages/banking/BankingDescriptivePaperPage";
import BankingDailyQuizPage from "@/pages/banking/BankingDailyQuizPage";
import BankingCutoffTrackerPage from "@/pages/banking/BankingCutoffTrackerPage";
import BankingPreviousYearsPage from "@/pages/banking/BankingPreviousYearsPage";
import { ExamProvider, EXAM_CONFIG } from "@/context/ExamContext";
// TGPSC Pages (State PSC - Multi-Exam Architecture)
import {
  TGPSCDashboardPage,
  TGPSCPrelimsHubPage,
  TGPSCMCQPracticePage,
  TGPSCPYQGenomePage,
  TGPSCHeatmapPage,
  TGPSCMainsHubPage,
  TGPSCNewsHubPage,
  TGPSCEditorialAnalyzerPage,
  TGPSCAnalyticsPage
} from "@/pages/tgpsc";
// APPSC Pages (State PSC - Andhra Pradesh)
import APPSCDashboardPage from "@/pages/appsc/APPSCDashboardPage";
import APPSCMainsHubPage from "@/pages/appsc/APPSCMainsHubPage";
import APPSCAnswerWritingPage from "@/pages/appsc/APPSCAnswerWritingPage";
import APPSCPaperSuitePage from "@/pages/appsc/APPSCPaperSuitePage";
// Editorial Analyzer
import EditorialAnalyzerPage from "@/pages/EditorialAnalyzerPage";
import FoundationHubPage from "@/pages/FoundationHubPage";
import FoundationSubjectPage from "@/pages/FoundationSubjectPage";
import FoundationBookPage from "@/pages/FoundationBookPage";
import FoundationChapterPage from "@/pages/FoundationChapterPage";

// Phase 2: Structured Tests
import TestsHomePage from "@/pages/TestsHomePage";
import TestAttemptPage from "@/pages/TestAttemptPage";

// Phase 3: 3-Year Roadmap
import RoadmapPage from "@/pages/RoadmapPage";
import WeeklyPlannerPage from "@/pages/WeeklyPlannerPage";
import SIRoadmapPage from "@/pages/SIRoadmapPage";
import CSATConceptNotesAdminPage from "@/pages/CSATConceptNotesAdminPage";
import PrepOSAdminPage from "@/pages/PrepOSAdminPage";
import ParentPortalPage from "@/pages/ParentPortalPage";
import NewsHubPage from "@/pages/NewsHubPage";
import PartnershipPricingPage from "@/pages/PartnershipPricingPage";
import InstitutePricingPage from "@/pages/InstitutePricingPage";
import AboutPage from "@/pages/AboutPage";
import CompanyHomePage from "@/pages/CompanyHomePage";
// FUTURE BACKLOG: Command Center (PCE) - Uncomment when ready to deploy
// import CommandCenterPage from "@/pages/CommandCenterPage";

// Re-export for backward compatibility with other files importing from App.js
export { API_BASE };

// CRITICAL: Ensure axios has NO baseURL set
// API_BASE already contains '/api', so setting baseURL would cause double prefix
// e.g., baseURL='/api' + url='/api/auth/me' = '/api/api/auth/me' (WRONG!)
axios.defaults.baseURL = '';  // Explicitly clear to prevent any issues

// Add axios interceptor to include token from localStorage
axios.interceptors.request.use(
  (config) => {
    const stored = localStorage.getItem("ranker-auth");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.token) {
          config.headers.Authorization = `Bearer ${parsed.token}`;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add axios response interceptor to handle session expired (multi-device login)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail || "";
      if (detail.includes("logged in from another device") || detail.includes("Session expired")) {
        // Clear local auth and redirect to login
        localStorage.removeItem("ranker-auth");
        // Show alert to user
        alert("You have been logged out because you logged in from another device.");
        window.location.href = "/auth";
      }
    }
    return Promise.reject(error);
  }
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <div data-testid="auth-loading">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

const HomeRedirect = () => {
  const { user, loading } = useAuth();
  const { isWhiteLabel, config } = useWhiteLabel();

  if (loading) return <div data-testid="auth-loading">Loading...</div>;
  
  // If user is logged in, check if they have selected an exam
  if (user) {
    // For white-label institutes: Auto-select UPSC and skip exam selector
    if (isWhiteLabel && config) {
      // Set UPSC as the default exam for institute users
      const savedExam = localStorage.getItem(`clarias_exam_${user.id}`);
      if (!savedExam) {
        localStorage.setItem(`clarias_exam_${user.id}`, 'UPSC');
        localStorage.setItem(`clarias_exam_selector_v2_${user.id}`, 'true');
      }
      return <Navigate to="/dashboard" replace />;
    }
    
    const savedExam = localStorage.getItem(`clarias_exam_${user.id}`);

    // Always show the exam selector on login/home (multi-exam support).
    return <Navigate to="/select-exam" replace />;
  }
  
  // If not logged in, show the UPSC landing page (LEGACY — this branch is
  // currently unreached because the `/` route uses `CompanyHomeWrapper`
  // which redirects to `/upsc`. Kept for backwards-compat with any code
  // path that still imports HomeRedirect.)
  return (
    <>
      <LandingPage />
      <WhyChooseCLARIASPopup />
    </>
  );
};

// Bottom Nav only for authenticated users
const AuthenticatedBottomNav = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  // Hide UPSC bottom nav on TGPSC/APPSC routes — they have their own navigation
  if (location.pathname.startsWith('/tgpsc') || location.pathname.startsWith('/appsc')) return null;
  return <BottomNav />;
};

// Route Change Listener - Dismisses toasts on navigation
// Note: Request cancellation is handled by individual components on unmount
const RouteChangeHandler = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Dismiss all active toasts on navigation
    dismissAllToasts();
  }, [location.pathname]);
  
  return null;
};

// Wrapper to use RouteChangeHandler inside BrowserRouter
const RouteChangeHandlerWrapper = () => {
  return <RouteChangeHandler />;
};

// Header Brand - Dynamic based on white-label context
const HeaderBrand = () => {
  const { isWhiteLabel, config, getInstituteName, getInstituteLogo } = useWhiteLabel();
  
  if (isWhiteLabel && config) {
    const name = getInstituteName();
    const logo = getInstituteLogo();
    const primaryColor = config.primary_color || '#FF6B00';
    
    return (
      <Link
        to="/dashboard"
        className="flex items-center gap-2 sm:gap-3 group flex-shrink-0"
        data-testid="brand-link"
      >
        {logo ? (
          <img 
            src={logo} 
            alt={name} 
            className="h-9 sm:h-10 object-contain group-hover:scale-105 transition-transform"
          />
        ) : (
          <div 
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform"
            style={{ background: `linear-gradient(135deg, ${primaryColor}, ${config.secondary_color || '#1E3A5F'})` }}
          >
            <span className="text-white font-bold text-lg">{name.charAt(0)}</span>
          </div>
        )}
        <div className="flex flex-col">
          <span 
            className="text-xl sm:text-2xl font-serif font-bold tracking-wide"
            style={{ color: config.secondary_color || '#1e3a8a' }}
          >
            {name}
          </span>
          <span 
            className="text-[10px] sm:text-xs font-medium tracking-wider hidden sm:block"
            style={{ color: primaryColor }}
          >
            UPSC Preparation
          </span>
        </div>
      </Link>
    );
  }
  
  // Default CLARIAS branding — original PNG torch+book logo, blue wordmark, orange "Clarity over Chaos" caption
  return (
    <Link
      to="/dashboard"
      className="flex items-center gap-2 sm:gap-3 group flex-shrink-0"
      data-testid="brand-link"
    >
      <img
        src="/clarias-torch-logo.png"
        alt="Clarias Logo"
        className="h-12 sm:h-14 object-contain group-hover:scale-105 transition-transform"
      />
      <div className="notranslate flex flex-col">
        <span className="text-xl sm:text-3xl font-serif font-bold tracking-wide text-[#1e3a8a]">Clarias</span>
        <span className="text-[10px] sm:text-xs font-medium tracking-wider hidden sm:block text-orange-600">Clarity over Chaos</span>
      </div>
    </Link>
  );
};

function AppShellContent() {
  const location = useLocation();
  const isPublicLanding = ["/", "/upsc", "/landing"].includes(location.pathname);
  // White header bar on public landing (so the original blue Clarias wordmark + orange caption read clearly).
  // Page body below the header remains dark navy for the V9 "Intelligence Layer" hero.
  const headerClass = isPublicLanding
    ? "bg-white sticky top-0 z-30 overflow-x-hidden border-b border-slate-200 shadow-sm"
    : "border-b border-stone-200 bg-white/95 backdrop-blur-md sticky top-0 z-20 overflow-x-hidden shadow-sm";
  const shellClass = isPublicLanding
    ? "min-h-screen bg-[#0B1220] text-white"
    : "min-h-screen bg-slate-50 text-slate-900";
  const navTextClass = isPublicLanding ? "text-slate-600" : "text-slate-600";

  return (
    <div className={shellClass}>
      <div className="flex flex-col min-h-screen">
        <header className={headerClass}>
          <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
            <HeaderBrand />
            <div className={`flex items-center gap-2 sm:gap-4 text-[11px] flex-shrink-0 ${navTextClass}`}>
              <NavActions />
            </div>
          </div>
        </header>

        <main className="flex-1 pb-20 sm:pb-16 overflow-x-hidden">
          <Routes>
            <Route path="/upsc" element={<PublicLandingPage />} />
              <Route path="/intelligence" element={<PublicLandingV7 />} />
              <Route path="/pyq" element={<PublicPYQExplorer />} />
              <Route path="/pyq/:tab" element={<PublicPYQExplorer />} />
              <Route path="/free-diagnostic" element={<FreeDiagnosticPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPageOTP />} />
            <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
            <Route path="/auth/google" element={<GoogleDirectCallback />} />
            <Route path="/login" element={<AuthPageOTP />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/partnership-pricing" element={<PartnershipPricingPage />} />
            <Route path="/institute-pricing" element={<InstitutePricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/launch-compliance" element={<LaunchCompliancePage />} />
            <Route path="/account-settings" element={<AccountSettingsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/free-answer" element={<FreeAnswerPage />} />
              <Route
                path="/weakness-map"
                element={
                  <PrivateRoute>
                    <WeaknessMapPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-evaluations"
                element={
                  <PrivateRoute>
                    <MyEvaluationsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/free-answer"
                element={
                  <PrivateRoute>
                    <FreeAnswerAdminPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <AdminDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/refer"
                element={
                  <PrivateRoute>
                    <ReferEarnPage />
                  </PrivateRoute>
                }
              />
              {/* DEPRECATED: Old generic mains answer page - use GS-specific suite pages instead */}
              {/* <Route
                path="/mains-answer"
                element={
                  <PrivateRoute>
                    <MainsAnswerPage />
                  </PrivateRoute>
                }
              /> */}
              <Route
                path="/expected"
                element={
                  <PrivateRoute>
                    <ExpectedQuestionsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/gs1-suite"
                element={
                  <PrivateRoute>
                    <GS1SuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/gs2-suite"
                element={
                  <PrivateRoute>
                    <GS2SuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/gs3-suite"
                element={
                  <PrivateRoute>
                    <GS3SuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/gs4-suite"
                element={
                  <PrivateRoute>
                    <GS4SuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/csat-suite"
                element={
                  <PrivateRoute>
                    <CSATSuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/intelligence-check"
                element={
                  <PrivateRoute>
                    <IntelligenceCheckPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/command"
                element={
                  <PrivateRoute>
                    <CommandScreenPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/diagnostic/csat"
                element={
                  <PrivateRoute>
                    <CSATDiagnosticPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/diagnostic/gs"
                element={
                  <PrivateRoute>
                    <GSDiagnosticPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/daily-intelligence"
                element={
                  <PrivateRoute>
                    <DailyIntelligenceV2Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/daily-intelligence-v2"
                element={
                  <PrivateRoute>
                    <DailyIntelligenceV2Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/news-hub"
                element={
                  <PrivateRoute>
                    <NewsHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/daily-intelligence-old"
                element={
                  <PrivateRoute>
                    <DailyIntelligencePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/editorial-analyzer"
                element={
                  <PrivateRoute>
                    <EditorialAnalyzerPage />
                  </PrivateRoute>
                }
              />
              {/* Foundation Course Routes */}
              <Route
                path="/foundation"
                element={
                  <PrivateRoute>
                    <FoundationHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/foundation/:subjectId"
                element={
                  <PrivateRoute>
                    <FoundationSubjectPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/foundation/:subjectId/book/:bookId"
                element={
                  <PrivateRoute>
                    <FoundationBookPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/foundation/:subjectId/book/:bookId/chapter/:chapterId"
                element={
                  <PrivateRoute>
                    <FoundationChapterPage />
                  </PrivateRoute>
                }
              />
              
              {/* Phase 2: Structured Tests */}
              <Route
                path="/tests"
                element={
                  <PrivateRoute>
                    <TestsHomePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tests/:testId"
                element={
                  <PrivateRoute>
                    <TestAttemptPage />
                  </PrivateRoute>
                }
              />
              
              {/* Phase 3: 3-Year Roadmap */}
              <Route
                path="/roadmap"
                element={
                  <PrivateRoute>
                    <RoadmapPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/roadmap/weekly"
                element={
                  <PrivateRoute>
                    <WeeklyPlannerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/si-roadmap"
                element={
                  <PrivateRoute>
                    <SIRoadmapPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/si-weekly"
                element={<Navigate to="/csat-suite?mode=si-test-series" replace />}
              />
              <Route
                path="/admin/csat-concept-notes"
                element={
                  <PrivateRoute>
                    <CSATConceptNotesAdminPage />
                  </PrivateRoute>
                }
              />
              
              {/* PrepOS Admin - Phase 4A */}
              <Route
                path="/prep-os/admin"
                element={
                  <PrivateRoute>
                    <PrepOSAdminPage />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="/parent-portal"
                element={
                  <PrivateRoute>
                    <ParentPortalPage />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="/mains-revision"
                element={
                  <PrivateRoute>
                    <MainsRevisionSchedulerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/evaluation"
                element={
                  <PrivateRoute>
                    <EvaluationPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/expected"
                element={
                  <PrivateRoute>
                    <ExpectedQuestionsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/csat"
                element={
                  <PrivateRoute>
                    <CSATSuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notes"
                element={
                  <PrivateRoute>
                    <NotesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pyq"
                element={
                  <PrivateRoute>
                    <PYQPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tests"
                element={
                  <PrivateRoute>
                    <TestSeriesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/gamification"
                element={
                  <PrivateRoute>
                    <GamificationPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/mentor"
                element={
                  <PrivateRoute>
                    <MentorPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/clarity"
                element={
                  <PrivateRoute>
                    <FeaturesShowcasePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/current-affairs"
                element={
                  <PrivateRoute>
                    <CurrentAffairsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/upgrade"
                element={
                  <PrivateRoute>
                    <UpgradePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/daily-targets"
                element={
                  <PrivateRoute>
                    <DailyTargetsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/essay-suite"
                element={
                  <PrivateRoute>
                    <EssaySuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/mains"
                element={
                  <PrivateRoute>
                    <MainsHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/mains-hub"
                element={
                  <PrivateRoute>
                    <MainsHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional"
                element={
                  <PrivateRoute>
                    <OptionalHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/anthropology"
                element={
                  <PrivateRoute>
                    <AnthropologySuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/anthropology/paper1"
                element={
                  <PrivateRoute>
                    <AnthropologyPaper1Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/anthropology/paper2"
                element={
                  <PrivateRoute>
                    <AnthropologyPaper2Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/sociology"
                element={
                  <PrivateRoute>
                    <SociologySuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/sociology/paper1"
                element={
                  <PrivateRoute>
                    <SociologyPaper1Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/sociology/paper2"
                element={
                  <PrivateRoute>
                    <SociologyPaper2Page />
                  </PrivateRoute>
                }
              />
              {/* Public Administration Routes */}
              <Route
                path="/optional/pub-admin"
                element={
                  <PrivateRoute>
                    <PubAdminSuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/pub-admin/paper1"
                element={
                  <PrivateRoute>
                    <PubAdminPaper1Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/pub-admin/paper2"
                element={
                  <PrivateRoute>
                    <PubAdminPaper2Page />
                  </PrivateRoute>
                }
              />
              {/* PSIR Routes */}
              <Route
                path="/optional/psir"
                element={
                  <PrivateRoute>
                    <PSIRSuitePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/psir/paper1"
                element={
                  <PrivateRoute>
                    <PSIRPaper1Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/optional/psir/paper2"
                element={
                  <PrivateRoute>
                    <PSIRPaper2Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/progress-report"
                element={
                  <PrivateRoute>
                    <ProgressReportPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/intelligence"
                element={
                  <PrivateRoute>
                    <IntelligenceEnginePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims"
                element={
                  <PrivateRoute>
                    <PrelimsHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/mcq-practice"
                element={
                  <PrivateRoute>
                    <PrelimsMCQPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/chapter-practice"
                element={
                  <PrivateRoute>
                    <ChapterPracticePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/ca-converter"
                element={
                  <PrivateRoute>
                    <PrelimsCAConverterPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/pyq-analyzer"
                element={
                  <PrivateRoute>
                    <PYQPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/csat-solver"
                element={
                  <PrivateRoute>
                    <PrelimsCSATPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/test-series"
                element={
                  <PrivateRoute>
                    <PrelimsTestSeriesPageNew />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/test-series-old"
                element={
                  <PrivateRoute>
                    <PrelimsTestSeriesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/newspaper-converter"
                element={
                  <PrivateRoute>
                    <PrelimsNewspaperConverterPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/heatmap"
                element={
                  <PrivateRoute>
                    <PrelimsHeatmapPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/genome"
                element={
                  <PrivateRoute>
                    <PYQGenomePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/pattern-prediction"
                element={
                  <PrivateRoute>
                    <PrelimsPatternPredictionPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/trap-detector"
                element={
                  <PrivateRoute>
                    <PrelimsTrapDetectorPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/analytics"
                element={
                  <PrivateRoute>
                    <PrelimsWeakAreaAnalyticsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/master"
                element={
                  <PrivateRoute>
                    <PrelimsMasterSubjectsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/polity-radial-mindmaps"
                element={
                  <PrivateRoute>
                    <PolityRadialMindMapsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/mindmap"
                element={
                  <PrivateRoute>
                    <MindMapPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/mindmap/:subjectId"
                element={
                  <PrivateRoute>
                    <MindMapPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/visual-resource"
                element={
                  <PrivateRoute>
                    <VisualResourcePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/master/:subjectId"
                element={
                  <PrivateRoute>
                    <PrelimsMasterSubjectPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/master/:subjectId/:chapterId"
                element={
                  <PrivateRoute>
                    <PrelimsMasterChapterPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/revision"
                element={
                  <PrivateRoute>
                    <PrelimsRevisionSchedulerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/pyq-papers"
                element={
                  <PrivateRoute>
                    <PYQPapersPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/si/pyq"
                element={
                  <PrivateRoute>
                    <SIPYQPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/mock-test-2025"
                element={
                  <PrivateRoute>
                    <MockTest2025Page />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/material-hub"
                element={
                  <PrivateRoute>
                    <MaterialHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/material-hub/:subject"
                element={
                  <PrivateRoute>
                    <MaterialHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prelims/material-hub/:subject/chapter/:chapterNum"
                element={
                  <PrivateRoute>
                    <MaterialHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/clarias-intelligence"
                element={
                  <PrivateRoute>
                    <SwitchingCostWallPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/strategy-dashboard"
                element={
                  <ProRoute
                    featureName="Strategy Analytics"
                    featureDescription="Mastery scores, weak-topic radar, weekly study plan and decision-grade insights — built only for serious PRO aspirants."
                  >
                    <StrategyDashboardPage />
                  </ProRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <PrivateRoute>
                    <AccountAccessPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/erca"
                element={
                  <PrivateRoute>
                    <ERCAPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/concept-intelligence"
                element={
                  <PrivateRoute>
                    <ConceptIntelligencePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/intelligence-digests"
                element={
                  <PrivateRoute>
                    <IntelligenceDigestsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/diagnostic-feedback"
                element={
                  <PrivateRoute>
                    <DiagnosticFeedbackPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/daily-challenge"
                element={
                  <PrivateRoute>
                    <DailyChallengePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/answer-arena"
                element={
                  <ProRoute
                    featureName="Answer Arena"
                    featureDescription="Daily UPSC mains questions, examiner-grade rubric evaluation and peer benchmarking against every CLARIAS aspirant."
                  >
                    <AnswerArenaPage />
                  </ProRoute>
                }
              />
              <Route
                path="/national-benchmark"
                element={
                  <ProRoute
                    featureName="National Rank Benchmark"
                    featureDescription="See your live percentile across MCQ accuracy, answer-writing quality, mock tests and consistency — vs every CLARIAS aspirant."
                  >
                    <NationalBenchmarkPage />
                  </ProRoute>
                }
              />
              <Route
                path="/mistake-tracker"
                element={
                  <PrivateRoute>
                    <MistakeTrackerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/clarity-ai"
                element={
                  <PrivateRoute>
                    <ClarityAssistantPage />
                  </PrivateRoute>
                }
              />
              {/* Exam Selector — Netflix-style track chooser (UPSC / SI) */}
              <Route
                path="/select-exam"
                element={
                  <PrivateRoute>
                    <ExamSelectorPage />
                  </PrivateRoute>
                }
              />
              {/* TGPSC Routes - State PSC (Multi-Exam Architecture) */}
              <Route
                path="/tgpsc"
                element={
                  <PrivateRoute>
                    <TGPSCDashboardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/prelims"
                element={
                  <PrivateRoute>
                    <TGPSCPrelimsHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/mcq"
                element={
                  <PrivateRoute>
                    <TGPSCMCQPracticePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/genome"
                element={
                  <PrivateRoute>
                    <TGPSCPYQGenomePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/heatmap"
                element={
                  <PrivateRoute>
                    <TGPSCHeatmapPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/pyq"
                element={
                  <PrivateRoute>
                    <TGPSCPYQGenomePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/mains"
                element={
                  <PrivateRoute>
                    <TGPSCMainsHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/news"
                element={
                  <PrivateRoute>
                    <TGPSCNewsHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/editorial"
                element={
                  <PrivateRoute>
                    <TGPSCEditorialAnalyzerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tgpsc/analytics"
                element={
                  <PrivateRoute>
                    <TGPSCAnalyticsPage />
                  </PrivateRoute>
                }
              />
              
              {/* APPSC Routes (Andhra Pradesh PSC — Mains Focus) */}
              <Route
                path="/appsc"
                element={
                  <PrivateRoute>
                    <APPSCDashboardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/appsc/mains"
                element={
                  <PrivateRoute>
                    <APPSCMainsHubPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/appsc/mains/:paperId"
                element={
                  <PrivateRoute>
                    <APPSCAnswerWritingPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/appsc/paper/:paperId"
                element={
                  <PrivateRoute>
                    <APPSCPaperSuitePage />
                  </PrivateRoute>
                }
              />

              {/* Banking Routes */}
              <Route
                path="/banking"
                element={
                  <PrivateRoute>
                    <BankingDashboardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/quant"
                element={
                  <PrivateRoute>
                    <BankingQuantPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/reasoning"
                element={
                  <PrivateRoute>
                    <BankingReasoningPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/english"
                element={
                  <PrivateRoute>
                    <BankingEnglishPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/awareness"
                element={
                  <PrivateRoute>
                    <BankingAwarenessPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/ai-coach"
                element={
                  <PrivateRoute>
                    <BankingAICoachPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/analytics"
                element={
                  <PrivateRoute>
                    <BankingAnalyticsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/learn"
                element={
                  <PrivateRoute>
                    <BankingLearnPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/learn-awareness"
                element={
                  <PrivateRoute>
                    <BankingAwarenessLearnPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/mock-test"
                element={
                  <PrivateRoute>
                    <BankingMockTestPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/sectional-test"
                element={
                  <PrivateRoute>
                    <BankingSectionalTestPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/mains-mock-test"
                element={
                  <PrivateRoute>
                    <BankingMainsMockTestPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/descriptive"
                element={
                  <PrivateRoute>
                    <BankingDescriptivePaperPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/daily-quiz"
                element={
                  <PrivateRoute>
                    <BankingDailyQuizPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/cutoff-tracker"
                element={
                  <PrivateRoute>
                    <BankingCutoffTrackerPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/previous-years"
                element={
                  <PrivateRoute>
                    <BankingPreviousYearsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/quant/practice"
                element={
                  <PrivateRoute>
                    <BankingPracticePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/reasoning/practice"
                element={
                  <PrivateRoute>
                    <BankingPracticePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/english/practice"
                element={
                  <PrivateRoute>
                    <BankingPracticePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/banking/practice"
                element={
                  <PrivateRoute>
                    <BankingUniversalPracticePage />
                  </PrivateRoute>
                }
              />
              {/* FUTURE BACKLOG: Command Center (PCE) - Uncomment when ready to deploy
              <Route
                path="/command-center"
                element={
                  <PrivateRoute>
                    <CommandCenterPage />
                  </PrivateRoute>
                }
              />
              */}
            </Routes>
          </main>
          
          {/* Bottom Navigation - Visible on authenticated pages */}
          <AuthenticatedBottomNav />
          
          {/* Mobile Bottom Navigation - Only on small screens */}
          <MobileBottomNav />
          
          {/* Legal Footer - Visible on ALL pages (non-negotiable) */}
          <LegalFooter variant="compact" />
          
          {/* Guided Tour for new users */}
          <GuidedTour />
        </div>
      <PhoneGate />
      <Toaster />
      <SonnerToaster position="top-center" richColors closeButton />
      <WhatsAppButton />
      <CookieBanner />
      <PWAInstallPrompt />
      <WhyChooseCLARIASPopup />
    </div>
  );
}
const NavActions = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDirector, setIsDirector] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  // Smart scroll/navigate to a section by id on the public landing page
  const goToSection = (id) => {
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    // Navigate to landing and let PublicLandingPage's hash useEffect scroll
    navigate('/#' + id);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  };
  const goToPricing = () => goToSection('pricing');
  
  // Get current exam from localStorage
  const currentExam = user ? localStorage.getItem(`clarias_exam_${user.id}`) : null;
  // Exam-scoped PRO: SI PRO and UPSC PRO are isolated entitlements.
  const isProForExam = user?.role === "ADMIN" || user?.is_admin
    || (currentExam === "SI" ? (user?.pro_exams || []).includes("SI") : user?.role === "PRO");
  
  // Check if user is a director/admin for PrepOS
  React.useEffect(() => {
    const checkDirectorRole = async () => {
      if (!user) return;
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${API_BASE}/prep-os/staff/my-role`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Check if user is director in any institute or is admin
          setIsDirector(data.role === 'director' || user.email === 'hariravi28@gmail.com');
        }
      } catch (err) {
        // Fallback: check if admin user
        setIsDirector(user.email === 'hariravi28@gmail.com');
      }
    };
    checkDirectorRole();
  }, [user]);
  
  const handleSwitchExam = () => {
    if (user) {
      // Clear the saved exam to force showing the exam selector
      localStorage.removeItem(`clarias_exam_${user.id}`);
      navigate('/select-exam');
    }
  };

  return (
    <div className="flex items-center gap-1.5 sm:gap-3">
      <LanguageSwitcher />
      {user ? (
        <>
          {/* Exam track badge — desktop only; on mobile it lives in the drawer grid */}
          {currentExam && EXAM_CONFIG[currentExam] && (
            <button
              onClick={handleSwitchExam}
              className="hidden md:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-xl text-xs font-bold text-stone-700 bg-stone-100 border border-stone-200 border-b-2 border-b-orange-500 hover:bg-stone-200 hover:text-stone-900 transition-all duration-150 whitespace-nowrap"
              data-testid="nav-switch-exam-btn"
              title="Switch exam track"
            >
              <span>{EXAM_CONFIG[currentExam].icon}</span>
              <span className="font-bold">{EXAM_CONFIG[currentExam].shortName}</span>
              <span className="hidden sm:inline text-stone-400 font-medium">· Switch</span>
            </button>
          )}

          {/* Desktop actions — inline pills (md and up) */}
          <div className="hidden md:flex items-center gap-2 lg:gap-2.5">
            <span
              className={isProForExam
                ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm whitespace-nowrap"
                : "inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-stone-200 text-stone-700 whitespace-nowrap"}
              data-testid="nav-user-role-badge"
            >
              {isProForExam ? "⭐ PRO" : "FREE"}
            </span>
            <Link
              to="/account"
              className="h-8 px-3 inline-flex items-center rounded-xl text-xs font-semibold bg-white border border-stone-200 text-stone-700 hover:bg-stone-100 hover:border-stone-300 transition-colors duration-150"
              data-testid="nav-account-button"
              title="Account"
            >
              Account
            </Link>
            <Link
              to="/parent-portal"
              className="h-8 px-3 inline-flex items-center rounded-xl text-xs font-semibold bg-white border border-stone-200 text-stone-700 hover:bg-stone-100 hover:border-stone-300 transition-colors duration-150"
              data-testid="nav-parent-portal-button"
              title="Parent Portal"
            >
              Parent
            </Link>
            {isDirector && (
              <Link
                to="/prep-os/admin"
                className="h-8 px-3 inline-flex items-center rounded-xl text-xs font-semibold bg-white border border-stone-200 text-stone-700 hover:bg-stone-100 hover:border-stone-300 transition-colors duration-150"
                data-testid="nav-prepos-admin-button"
                title="PrepOS Admin - Institute Management"
              >
                PrepOS
              </Link>
            )}
            {user.email === 'hariravi28@gmail.com' && (
              <Link
                to="/admin"
                className="h-8 px-3 inline-flex items-center rounded-xl text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 border border-orange-600/20 shadow-sm transition-colors duration-150"
                data-testid="nav-admin-button"
                title="Admin Dashboard"
              >
                Admin
              </Link>
            )}
            <button
              onClick={logout}
              className="h-8 px-3 inline-flex items-center rounded-xl text-xs font-semibold bg-stone-900 text-white hover:bg-stone-800 transition-colors duration-150"
              data-testid="nav-logout-button"
              title="Logout"
            >
              Logout
            </button>
          </div>

          {/* Mobile hamburger (below md) */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-colors"
            data-testid="nav-mobile-menu-button"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Mobile drawer — logged-in menu as a clean grid/list */}
          {mobileMenuOpen && createPortal((
            <div className="fixed inset-0 z-[100] md:hidden" data-testid="mobile-menu-drawer">
              <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
                onClick={() => setMobileMenuOpen(false)}
              />
              <div className="absolute top-0 right-0 h-full w-72 max-w-[82%] bg-white shadow-2xl flex flex-col p-6 animate-in slide-in-from-right duration-200">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-serif font-bold text-[#0B1220]">Menu</span>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700"
                      data-testid="mobile-nav-user-role-badge"
                    >
                      {isProForExam ? "⭐ PRO" : "FREE"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 rounded-full text-slate-700 hover:bg-slate-100"
                    data-testid="mobile-menu-close-button"
                    aria-label="Close menu"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {/* 2-column grid of actions */}
                <div className="grid grid-cols-2 gap-2">
                  {currentExam && EXAM_CONFIG[currentExam] && (
                    <button
                      type="button"
                      onClick={() => { setMobileMenuOpen(false); handleSwitchExam(); }}
                      className="flex flex-col items-center gap-1 px-3 py-4 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                      data-testid="mobile-nav-switch-exam-btn"
                    >
                      <span className="text-xl">{EXAM_CONFIG[currentExam].icon}</span>
                      <span className="text-xs font-semibold">Switch Exam</span>
                    </button>
                  )}
                  <Link
                    to="/account"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex flex-col items-center gap-1 px-3 py-4 rounded-2xl bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                    data-testid="mobile-nav-account-button"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    <span className="text-xs font-semibold">Account</span>
                  </Link>
                  <Link
                    to="/parent-portal"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex flex-col items-center gap-1 px-3 py-4 rounded-2xl bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                    data-testid="mobile-nav-parent-portal-button"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                    <span className="text-xs font-semibold">Parent</span>
                  </Link>
                  {isDirector && (
                    <Link
                      to="/prep-os/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex flex-col items-center gap-1 px-3 py-4 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 transition-colors"
                      data-testid="mobile-nav-prepos-admin-button"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                      <span className="text-xs font-semibold">PrepOS</span>
                    </Link>
                  )}
                  {user.email === 'hariravi28@gmail.com' && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex flex-col items-center gap-1 px-3 py-4 rounded-2xl bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                      data-testid="mobile-nav-admin-button"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      <span className="text-xs font-semibold">Admin</span>
                    </Link>
                  )}
                </div>
                <button
                  onClick={() => { setMobileMenuOpen(false); logout(); }}
                  className="mt-4 w-full text-center px-4 py-3 rounded-2xl text-base font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                  data-testid="mobile-nav-logout-button"
                >
                  Logout
                </button>
              </div>
            </div>
          ), document.body)}
        </>
      ) : (
        <>
          {(() => {
            const isPublicLanding = ["/", "/upsc", "/landing"].includes(location.pathname);
            // White header on public landing — use dark-on-light styles (same as inner app)
            const ghostBtn = "text-slate-600 hover:text-slate-900 hover:bg-slate-100";
            const loginBtn = "bg-slate-900 text-white hover:bg-slate-800";
            return (
              <>
                {/* Desktop pills */}
                <div className="hidden md:flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('open-clarias-comparison'))}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${ghostBtn}`}
                    data-testid="nav-why-clarias-button"
                  >
                    Why Choose Clarias
                  </button>
                  <button
                    type="button"
                    onClick={() => goToSection('voices')}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${ghostBtn}`}
                    data-testid="nav-aspirants-button"
                  >
                    Aspirants
                  </button>
                  <Link
                    to="/free-answer"
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${ghostBtn}`}
                    data-testid="nav-free-answer-button"
                  >
                    Free Answer
                  </Link>
                  <button
                    type="button"
                    onClick={goToPricing}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${ghostBtn}`}
                    data-testid="nav-pricing-button"
                  >
                    Pricing
                  </button>
                  <Link
                    to="/auth"
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${loginBtn}`}
                    data-testid="nav-login-button"
                  >
                    Login
                  </Link>
                </div>

                {/* Mobile hamburger */}
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(true)}
                  className="md:hidden p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="nav-mobile-menu-button"
                  aria-label="Open menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                {/* Mobile drawer */}
                {mobileMenuOpen && createPortal((
                  <div className="fixed inset-0 z-[100] md:hidden" data-testid="mobile-menu-drawer">
                    <div
                      className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    />
                    <div className="absolute top-0 right-0 h-full w-72 max-w-[82%] bg-white shadow-2xl flex flex-col p-6 animate-in slide-in-from-right duration-200">
                      <div className="flex items-center justify-between mb-8">
                        <span className="text-lg font-serif font-bold text-[#0B1220]">Menu</span>
                        <button
                          type="button"
                          onClick={() => setMobileMenuOpen(false)}
                          className="p-2 rounded-full text-slate-700 hover:bg-slate-100"
                          data-testid="mobile-menu-close-button"
                          aria-label="Close menu"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <nav className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => { setMobileMenuOpen(false); window.dispatchEvent(new Event('open-clarias-comparison')); }}
                          className="text-left px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                          data-testid="mobile-nav-why-clarias-button"
                        >
                          Why Choose Clarias
                        </button>
                        <button
                          type="button"
                          onClick={() => goToSection('voices')}
                          className="text-left px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                          data-testid="mobile-nav-aspirants-button"
                        >
                          Aspirants
                        </button>
                        <Link
                          to="/free-answer"
                          onClick={() => setMobileMenuOpen(false)}
                          className="text-left px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                          data-testid="mobile-nav-free-answer-button"
                        >
                          Free Answer
                        </Link>
                        <button
                          type="button"
                          onClick={goToPricing}
                          className="text-left px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                          data-testid="mobile-nav-pricing-button"
                        >
                          Pricing
                        </button>
                        <Link
                          to="/auth"
                          onClick={() => setMobileMenuOpen(false)}
                          className="mt-4 text-center px-4 py-3 rounded-xl text-base font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                          data-testid="mobile-nav-login-button"
                        >
                          Login
                        </Link>
                      </nav>
                    </div>
                  </div>
                ), document.body)}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <WhiteLabelProvider>
        <AuthProvider>
          <ExamProvider>
            <BrowserRouter>
              <ScrollToTop />
              <RouteChangeHandlerWrapper />
              <Routes>
                {/* Company homepage - rendered without header */}
                <Route path="/" element={<CompanyHomeWrapper />} />
                {/* All other routes use AppShell with header */}
                <Route path="/*" element={<AppShellContent />} />
              </Routes>
            </BrowserRouter>
          </ExamProvider>
        </AuthProvider>
      </WhiteLabelProvider>
    </HelmetProvider>
  );
}

// Wrapper for company homepage - checks if user is logged in
const CompanyHomeWrapper = () => {
  const { user, loading } = useAuth();
  const { isWhiteLabel, config } = useWhiteLabel();

  if (loading) return <div data-testid="auth-loading">Loading...</div>;
  
  // If user is logged in, redirect to appropriate dashboard
  if (user) {
    // For white-label institutes: Auto-select UPSC and skip exam selector
    if (isWhiteLabel && config) {
      const savedExam = localStorage.getItem(`clarias_exam_${user.id}`);
      if (!savedExam) {
        localStorage.setItem(`clarias_exam_${user.id}`, 'UPSC');
        localStorage.setItem(`clarias_exam_selector_v2_${user.id}`, 'true');
      }
      return <Navigate to="/dashboard" replace />;
    }
    
    const savedExam = localStorage.getItem(`clarias_exam_${user.id}`);

    // Always show the exam selector on login/home so users can pick among the
    // multiple exams. The selector pre-highlights their last choice (savedExam)
    // and "Continue Preparing" takes them into that exam's dashboard.
    return <Navigate to="/select-exam" replace />;
  }
  
  // If not logged in, show the rich UPSC landing page (Generic AI vs CLARIAS
  // comparison, Optionals, Daily CA, Pricing). The slimmer `LandingPage.js`
  // was missing these sections, hurting conversion.
  return <Navigate to="/upsc" replace />;
};

export default App;
